import apiClient from "@/api/client";
import type {
  ClientInvoice,
  ClientInvoiceAiAnalysis,
  ClientInvoiceEvent,
  ClientInvoiceFormData,
  ClientInvoiceLineItem,
  ConsortiumAgreement,
  JointVentureInvoice,
  CostMatrixData,
  CostMethod,
  GlobalCostMatrixData,
  GroupedCostMatrixData,
  EmployeeExpense,
  EmployeeExpenseEvent,
  EmployeeSalaryCost,
  GlobalCostAllocationItem,
  GlobalCostAllocationSummary,
  InternalCostAllocation,
  InternalCostOneTime,
  InternalCostRecurring,
  MonthlyPayrollActual,
  PlannedRevenue,
  ProjectEmployeeAllocation,
  TheoreticalCompany,
} from "@/features/finances/types";
import type { FinancialSummary, CashflowEvent } from "@/features/enterprises/types";

const wrap = <T>(res: { data: { data: T } }) => res.data.data;

export interface PlannedRevenueMeta {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  total_amount: string;
}

export interface PlannedRevenueParams {
  project?: string;
  is_auto_generated?: string;
  date_from?: string;
  date_to?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
  blocking_subtask?: string;
}

// ── Client Invoices (Faktury PRZ) ──────────────────────────────────────────

export const clientInvoicesApi = {
  list: (params?: Record<string, string>) =>
    apiClient.get<{ data: ClientInvoice[] }>("/finances/client-invoices/", { params }).then(wrap),

  get: (id: string) =>
    apiClient.get<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/`).then(wrap),

  create: (data: ClientInvoiceFormData & { invoice_file?: File }) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => { if (v !== undefined && v !== null) form.append(k, v as string | File); });
    return apiClient.post<{ data: ClientInvoice }>("/finances/client-invoices/", form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(wrap);
  },

  update: (id: string, data: Partial<ClientInvoiceFormData> & { invoice_file?: File | null }) => {
    if (data.invoice_file instanceof File) {
      const form = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (v !== undefined && v !== null) form.append(k, v as string | File);
      });
      return apiClient.patch<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then(wrap);
    }
    return apiClient.patch<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/`, data).then(wrap);
  },

  delete: (id: string) =>
    apiClient.delete(`/finances/client-invoices/${id}/`),

  // Tag/untag a PRZ invoice as belonging to a consortium agreement (gated by
  // consortium access in the serializer).
  setConsortium: (id: string, consortium_agreement: string | null) =>
    apiClient.patch<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/`, { consortium_agreement }).then(wrap),

  updatePayment: (id: string, data: { new_payment_amount: string; payment_date?: string; payment_comment?: string }) =>
    apiClient.patch<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/update_payment/`, data).then(wrap),

  getEvents: (id: string) =>
    apiClient.get<{ data: ClientInvoiceEvent[] }>(`/finances/client-invoices/${id}/events/`).then(wrap),

  addComment: (id: string, message: string, mentionIds?: string[], files?: File[]) => {
    if (files && files.length > 0) {
      const form = new FormData();
      form.append("message", message);
      (mentionIds ?? []).forEach((mid) => form.append("mention_ids", mid));
      files.forEach((f) => form.append("files", f));
      return apiClient.post<{ data: ClientInvoiceEvent }>(`/finances/client-invoices/${id}/add_comment/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then(wrap);
    }
    return apiClient.post<{ data: ClientInvoiceEvent }>(`/finances/client-invoices/${id}/add_comment/`, { message, mention_ids: mentionIds ?? [] }).then(wrap);
  },

  deletePaymentEvent: (invoiceId: string, eventId: string) =>
    apiClient.delete<{ data: ClientInvoice }>(`/finances/client-invoices/${invoiceId}/events/${eventId}/`).then(wrap),

  // Ponowna analiza AI pliku faktury (Prezes / księgowość) — działa synchronicznie.
  analyze: (id: string) =>
    apiClient.post<{ data: ClientInvoiceAiAnalysis | null }>(`/finances/client-invoices/${id}/analyze/`).then(wrap),

  approve: (id: string) =>
    apiClient.post<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/approve/`).then(wrap),

  revokeApproval: (id: string) =>
    apiClient.post<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/revoke-approval/`).then(wrap),

  setExpectedDate: (id: string, date: string | null) =>
    apiClient.patch<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/set-expected-date/`, {
      expected_payment_date: date ?? "",
    }).then(wrap),

  listLineItems: (id: string) =>
    apiClient.get<{ data: ClientInvoiceLineItem[] }>(`/finances/client-invoices/${id}/line-items/`).then(wrap),

  createLineItem: (id: string, data: { name: string; unit_value: string; quantity: string; position?: number }) =>
    apiClient.post<{ data: ClientInvoiceLineItem }>(`/finances/client-invoices/${id}/line-items/`, data).then(wrap),

  updateLineItem: (invoiceId: string, itemId: string, data: Partial<{ name: string; unit_value: string; quantity: string; position: number }>) =>
    apiClient.patch<{ data: ClientInvoiceLineItem }>(`/finances/client-invoices/${invoiceId}/line-items/${itemId}/`, data).then(wrap),

  deleteLineItem: (invoiceId: string, itemId: string) =>
    apiClient.delete(`/finances/client-invoices/${invoiceId}/line-items/${itemId}/`),

  convertProforma: (id: string, data: FormData) =>
    apiClient.post<{ data: ClientInvoice }>(`/finances/client-invoices/${id}/convert-proforma/`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(wrap),
};

// ── Joint venture / consortium ─────────────────────────────────────────────

export interface JointVentureInvoiceFormData {
  client_invoice: string;
  consortium_agreement?: string | null;
  partner_name?: string;
  invoice_number?: string;
  amount_net: string;
  amount_gross: string;
  invoice_date?: string | null;
  notes?: string;
  status?: "draft" | "pending";
}

export const jvInvoicesApi = {
  listByInvoice: (clientInvoiceId: string) =>
    apiClient
      .get<{ data: JointVentureInvoice[] }>("/finances/jv-invoices/", { params: { client_invoice: clientInvoiceId } })
      .then(wrap),
  listAll: () => apiClient.get<{ data: JointVentureInvoice[] }>("/finances/jv-invoices/").then(wrap),
  create: (data: JointVentureInvoiceFormData & { invoice_file?: File | null }) => {
    if (data.invoice_file instanceof File) {
      const form = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v !== undefined && v !== null) form.append(k, v as string | File); });
      return apiClient.post<{ data: JointVentureInvoice }>("/finances/jv-invoices/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then(wrap);
    }
    return apiClient.post<{ data: JointVentureInvoice }>("/finances/jv-invoices/", data).then(wrap);
  },
  update: (id: string, data: Partial<JointVentureInvoiceFormData> & { invoice_file?: File | null; remove_file?: boolean }) => {
    if (data.invoice_file instanceof File) {
      const form = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v !== undefined && v !== null) form.append(k, v as string | File); });
      return apiClient.patch<{ data: JointVentureInvoice }>(`/finances/jv-invoices/${id}/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then(wrap);
    }
    return apiClient.patch<{ data: JointVentureInvoice }>(`/finances/jv-invoices/${id}/`, data).then(wrap);
  },
  remove: (id: string) => apiClient.delete(`/finances/jv-invoices/${id}/`),
  submit: (id: string) =>
    apiClient.post<{ data: JointVentureInvoice }>(`/finances/jv-invoices/${id}/submit/`).then(wrap),
  approve: (id: string) =>
    apiClient.post<{ data: JointVentureInvoice }>(`/finances/jv-invoices/${id}/approve/`).then(wrap),
  reject: (id: string) =>
    apiClient.post<{ data: JointVentureInvoice }>(`/finances/jv-invoices/${id}/reject/`).then(wrap),
  prepareTransfer: (id: string) =>
    apiClient.post<{ data: JointVentureInvoice }>(`/finances/jv-invoices/${id}/prepare-transfer/`).then(wrap),
  markPaid: (id: string, paid_at?: string) =>
    apiClient.post<{ data: JointVentureInvoice }>(`/finances/jv-invoices/${id}/mark-paid/`, paid_at ? { paid_at } : {}).then(wrap),
  markBooked: (id: string) =>
    apiClient.post<{ data: JointVentureInvoice }>(`/finances/jv-invoices/${id}/mark-booked/`).then(wrap),
};

export interface ConsortiumAgreementFormData {
  name: string;
  partner_name: string;
  our_share_percent: string;
  notes?: string;
  is_active?: boolean;
  partner_nip?: string;
  partner_krs?: string;
  partner_regon?: string;
  partner_address?: string;
  partner_bank_account?: string;
  partner_bank_name?: string;
}

export const consortiumAgreementsApi = {
  list: (activeOnly = false) =>
    apiClient
      .get<{ data: ConsortiumAgreement[] }>("/finances/consortium-agreements/", {
        params: activeOnly ? { active: "true" } : {},
      })
      .then(wrap),
  create: (data: ConsortiumAgreementFormData) =>
    apiClient.post<{ data: ConsortiumAgreement }>("/finances/consortium-agreements/", data).then(wrap),
  update: (id: string, data: Partial<ConsortiumAgreementFormData>) =>
    apiClient.patch<{ data: ConsortiumAgreement }>(`/finances/consortium-agreements/${id}/`, data).then(wrap),
  remove: (id: string) => apiClient.delete(`/finances/consortium-agreements/${id}/`),
};

export interface ConsortiumAccessGrant {
  id: string;
  user: string;
  user_name: string;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
}

export interface ConsortiumAccessUserRow {
  user: { id: string; first_name: string; last_name: string; email: string; role: string };
  has_access: boolean;
}

export const consortiumAccessApi = {
  list: () => apiClient.get<{ data: ConsortiumAccessGrant[] }>("/ceo/consortium-access/").then(wrap),
  users: () => apiClient.get<{ data: ConsortiumAccessUserRow[] }>("/ceo/consortium-access/users/").then(wrap),
  grant: (userId: string) =>
    apiClient.post<{ data: ConsortiumAccessGrant }>("/ceo/consortium-access/", { user: userId }).then(wrap),
  revoke: (grantId: string) => apiClient.delete(`/ceo/consortium-access/${grantId}/`),
};

// ── Internal Costs — One-Time ──────────────────────────────────────────────

export const oneTimeCostsApi = {
  list: (params?: Record<string, string>) =>
    apiClient.get<{ data: InternalCostOneTime[] }>("/finances/one-time-costs/", { params }).then(wrap),

  create: (data: Omit<InternalCostOneTime, "id" | "project_name" | "created_by" | "created_by_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: InternalCostOneTime }>("/finances/one-time-costs/", data).then(wrap),

  update: (id: string, data: Partial<InternalCostOneTime>) =>
    apiClient.patch<{ data: InternalCostOneTime }>(`/finances/one-time-costs/${id}/`, data).then(wrap),

  delete: (id: string) =>
    apiClient.delete(`/finances/one-time-costs/${id}/`),

  markPaid: (id: string, paid_at: string) =>
    apiClient.post<{ data: InternalCostOneTime }>(`/finances/one-time-costs/${id}/mark-paid/`, { paid_at }).then(wrap),
};

// ── Internal Costs — Recurring ─────────────────────────────────────────────

export const recurringCostsApi = {
  list: (params?: Record<string, string>) =>
    apiClient.get<{ data: InternalCostRecurring[] }>("/finances/recurring-costs/", { params }).then(wrap),

  create: (data: Partial<InternalCostRecurring>) =>
    apiClient.post<{ data: InternalCostRecurring }>("/finances/recurring-costs/", data).then(wrap),

  update: (id: string, data: Partial<InternalCostRecurring>) =>
    apiClient.patch<{ data: InternalCostRecurring }>(`/finances/recurring-costs/${id}/`, data).then(wrap),

  delete: (id: string) =>
    apiClient.delete(`/finances/recurring-costs/${id}/`),

  /** Shared history of ALL monthly-invoice files of a recurring cost. */
  expenseHistory: (id: string) =>
    apiClient.get<{ data: EmployeeExpenseEvent[] }>(`/finances/recurring-costs/${id}/expense-history/`).then(wrap),

  /** Recurring costs with no invoice attached for a month — the whole backlog
   *  when no period is given, or just that month for the monthly panel. */
  missingInvoices: (period?: { year: number; month: number }) =>
    apiClient
      .get<{ data: MissingInvoiceRow[]; meta: MissingInvoiceMeta }>(
        "/finances/recurring-costs/missing-invoices/",
        { params: period ? { year: String(period.year), month: String(period.month) } : undefined },
      )
      .then((r) => r.data),

  /** Close a month without an invoice („faktury nie było") → counts as 0,00 zł. */
  skipMonth: (id: string, data: { period_year: number; period_month: number; note?: string }) =>
    apiClient.post(`/finances/recurring-costs/${id}/skip-month/`, data).then(wrap),

  unskipMonth: (id: string, data: { period_year: number; period_month: number }) =>
    apiClient.post(`/finances/recurring-costs/${id}/unskip-month/`, data).then(wrap),
};

export interface MissingInvoiceRow {
  id: string;
  description: string;
  project_name: string | null;
  company_name: string | null;
  start_date: string;
  end_date: string | null;
  /** What the forecast substitutes for the missing month (the „orientacyjna" figure). */
  estimated_amount: string;
  missing_count: number;
  missing: { year: number; month: number; label: string }[];
}

export interface MissingInvoiceMeta {
  costs_with_gaps: number;
  missing_total: number;
  period: { year: number; month: number } | null;
  /** From this month on, months are settled with real invoices instead of the average. */
  real_amount_from: { year: number; month: number };
  formula: string;
}

// ── Internal Cost Invoices ────────────────────────────────────────────────

export const internalCostInvoiceApi = {
  getForOneTime: (costId: string) =>
    apiClient.get<{ data: import("../types").InternalCostInvoice | null }>(`/finances/one-time-costs/${costId}/invoice/`).then(wrap),

  uploadForOneTime: (costId: string, file: File) => {
    const form = new FormData();
    form.append("invoice_file", file);
    return apiClient.post<{ data: import("../types").InternalCostInvoice }>(`/finances/one-time-costs/${costId}/invoice/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(wrap);
  },

  deleteForOneTime: (costId: string) =>
    apiClient.delete(`/finances/one-time-costs/${costId}/invoice/`),

  listForRecurring: (costId: string) =>
    apiClient.get<{ data: import("../types").InternalCostInvoice[] }>(`/finances/recurring-costs/${costId}/invoices/`).then(wrap),

  uploadForRecurring: (costId: string, month: string, file: File) => {
    const form = new FormData();
    form.append("month", month);
    form.append("invoice_file", file);
    return apiClient.post<{ data: import("../types").InternalCostInvoice }>(`/finances/recurring-costs/${costId}/invoice/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(wrap);
  },

  deleteForRecurring: (costId: string, month: string) =>
    apiClient.delete(`/finances/recurring-costs/${costId}/invoice/?month=${month}`),
};

// ── Per-Cost Allocations ──────────────────────────────────────────────────

export const costAllocationApi = {
  getForOneTime: (costId: string) =>
    apiClient.get<{ data: InternalCostAllocation[] }>(`/finances/one-time-costs/${costId}/allocations/`).then(wrap),

  setForOneTime: (costId: string, allocations: { project: string; fraction: number }[]) =>
    apiClient.post<{ data: InternalCostAllocation[] }>(`/finances/one-time-costs/${costId}/allocations/bulk-set/`, { allocations }).then(wrap),

  resetForOneTime: (costId: string) =>
    apiClient.delete(`/finances/one-time-costs/${costId}/allocations/reset/`),

  getForRecurring: (costId: string) =>
    apiClient.get<{ data: InternalCostAllocation[] }>(`/finances/recurring-costs/${costId}/allocations/`).then(wrap),

  setForRecurring: (costId: string, allocations: { project: string; fraction: number }[]) =>
    apiClient.post<{ data: InternalCostAllocation[] }>(`/finances/recurring-costs/${costId}/allocations/bulk-set/`, { allocations }).then(wrap),

  resetForRecurring: (costId: string) =>
    apiClient.delete(`/finances/recurring-costs/${costId}/allocations/reset/`),
};

// ── Salary Costs ──────────────────────────────────────────────────────────

export const salaryCostsApi = {
  list: (params?: Record<string, string>) =>
    apiClient.get<{ data: EmployeeSalaryCost[] }>("/finances/salary-costs/", { params }).then(wrap),

  create: (data: Omit<EmployeeSalaryCost, "id" | "employee_name" | "company_name" | "is_primary" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: EmployeeSalaryCost }>("/finances/salary-costs/", data).then(wrap),

  update: (id: string, data: Partial<EmployeeSalaryCost>) =>
    apiClient.patch<{ data: EmployeeSalaryCost }>(`/finances/salary-costs/${id}/`, data).then(wrap),

  delete: (id: string) =>
    apiClient.delete(`/finances/salary-costs/${id}/`),

  setPrimary: (id: string) =>
    apiClient.post<{ data: EmployeeSalaryCost }>(`/finances/salary-costs/${id}/set_primary/`).then(wrap),
};

// ── Employee Allocations ──────────────────────────────────────────────────

export const employeeAllocationsApi = {
  list: (params?: Record<string, string>) =>
    apiClient.get<{ data: ProjectEmployeeAllocation[] }>("/finances/employee-allocations/", { params }).then(wrap),

  create: (data: Omit<ProjectEmployeeAllocation, "id" | "employee_name" | "project_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: ProjectEmployeeAllocation }>("/finances/employee-allocations/", data).then(wrap),

  update: (id: string, data: Partial<ProjectEmployeeAllocation>) =>
    apiClient.patch<{ data: ProjectEmployeeAllocation }>(`/finances/employee-allocations/${id}/`, data).then(wrap),

  delete: (id: string) =>
    apiClient.delete(`/finances/employee-allocations/${id}/`),
};

// ── Proportional Distribution Exclusions ─────────────────────────────────

export interface PropDistExclusion {
  id: string;
  employee: string;
  project: string;
  year: number;
  month: number;
}

export const propDistExclusionsApi = {
  list: (params: { employee: string; year: number; month: number }) =>
    apiClient
      .get<{ data: PropDistExclusion[] }>("/finances/prop-dist-exclusions/", { params })
      .then(wrap),

  create: (data: { employee: string; project: string; year: number; month: number }) =>
    apiClient.post<{ data: PropDistExclusion }>("/finances/prop-dist-exclusions/", data).then(wrap),

  delete: (id: string) =>
    apiClient.delete(`/finances/prop-dist-exclusions/${id}/`),

  bulkDelete: (data: { employee: string; project: string; year: number; month: number }) =>
    apiClient.delete("/finances/prop-dist-exclusions/bulk-delete/", { data }),

  bulkCreate: async (items: { employee: string; project: string; year: number; month: number }[]) => {
    for (const item of items) {
      try {
        await apiClient.post("/finances/prop-dist-exclusions/", item);
      } catch { /* already exists — skip */ }
    }
  },
};

// ── Planned Revenues ──────────────────────────────────────────────────────

export interface PlannedRevenueFormData {
  description?: string;
  notes?: string;
  amount?: string;
  expected_date?: string;
  blocking_subtask_ids?: string[];
}

export const plannedRevenuesApi = {
  list: (params?: PlannedRevenueParams) =>
    apiClient
      .get<{ data: PlannedRevenue[]; meta: PlannedRevenueMeta }>("/finances/planned-revenues/", { params })
      .then((res) => ({ items: res.data.data, meta: res.data.meta })),

  listBySubtask: (subtaskId: string) =>
    apiClient
      .get<{ data: PlannedRevenue[]; meta: PlannedRevenueMeta }>("/finances/planned-revenues/", { params: { blocking_subtask: subtaskId } })
      .then((res) => res.data.data),

  create: (data: { project: string } & PlannedRevenueFormData) =>
    apiClient.post<{ data: PlannedRevenue }>("/finances/planned-revenues/", data).then(wrap),

  update: (id: string, data: PlannedRevenueFormData) =>
    apiClient.patch<{ data: PlannedRevenue }>(`/finances/planned-revenues/${id}/`, data).then(wrap),

  convertToDraft: (id: string, opts?: { is_advance_invoice?: boolean }) =>
    apiClient.post<{ data: PlannedRevenue }>(`/finances/planned-revenues/${id}/convert-to-draft/`, opts ?? {}).then(wrap),

  delete: (id: string) =>
    apiClient.delete(`/finances/planned-revenues/${id}/`),
};

// ── Global Cost Allocations ───────────────────────────────────────────────

export const globalCostAllocationsApi = {
  summary: () =>
    apiClient.get<{ data: GlobalCostAllocationSummary }>("/finances/global-cost-allocations/").then(wrap),

  bulkSet: (allocations: Pick<GlobalCostAllocationItem, "project" | "fraction">[]) =>
    apiClient.post("/finances/global-cost-allocations/bulk-set/", { allocations }),

  reset: () =>
    apiClient.delete("/finances/global-cost-allocations/reset/"),

  costMatrix: (year: number, month: number) =>
    apiClient
      .get<{ data: GlobalCostMatrixData }>("/finances/global-cost-allocations/cost-matrix/", {
        params: { year, month },
      })
      .then(wrap),
};

// ── Project Financial APIs (on project endpoint) ──────────────────────────

export const projectFinanceApi = {
  summary: (projectId: string, method?: CostMethod) =>
    apiClient
      .get<{ data: FinancialSummary }>(`/projects/${projectId}/financial-summary/`, {
        params: method ? { method } : {},
      })
      .then(wrap),

  cashflowEvents: (projectId: string, method?: CostMethod) =>
    apiClient
      .get<{ data: CashflowEvent[] }>(`/projects/${projectId}/cashflow-events/`, {
        params: method ? { method } : {},
      })
      .then(wrap),

  setStatus: (projectId: string, data: { status: string; suspension_fraction?: number; reason?: string }) =>
    apiClient.post<{ data: unknown }>(`/projects/${projectId}/set-status/`, data).then(wrap),

  updateValue: (projectId: string, data: { total_value?: string | null; planned_end_date?: string | null; planned_end_date_ceo?: string | null }) =>
    apiClient.patch<{ data: unknown }>(`/projects/${projectId}/update-value/`, data).then(wrap),

  unassignedRecurringBreakdown: (projectId: string) =>
    apiClient.get<{ data: { year: number; month: number; amount: string; items: { description: string; fraction_pct: number; amount: string }[] }[] }>(
      `/projects/${projectId}/unassigned-recurring-breakdown/`
    ).then(wrap),

  narzutPeopleBreakdown: (projectId: string) =>
    apiClient.get<{ data: { employee_name: string; year: number; month: number; full_salary: string; share_pct: number; amount: string }[] }>(
      `/projects/${projectId}/narzut-people-breakdown/`
    ).then(wrap),

  engagementCurve: (projectId: string) =>
    apiClient.get<{ data: { peak_load: number; rows: { year: number; month: number; load: number; activity_pct: number; source: "fact" | "forecast" }[] } }>(
      `/projects/${projectId}/engagement-curve/`
    ).then(wrap),

  narzutFixedBreakdown: (projectId: string) =>
    apiClient.get<{ data: { year: number; month: number; amount: string; items: { description: string; fraction_pct: number; amount: string }[] }[] }>(
      `/projects/${projectId}/narzut-fixed-breakdown/`
    ).then(wrap),

  recurringCostsByMonth: (projectId: string) =>
    apiClient.get<{ data: { year: number; month: number; amount: string; items: { description: string; amount: string; type: "assigned" | "unassigned"; fraction_pct?: number }[] }[] }>(
      `/projects/${projectId}/recurring-costs-by-month/`
    ).then(wrap),

  salaryCostsByMonth: (projectId: string) =>
    apiClient.get<{ data: { year: number; month: number; amount: string; items: { employee_name: string; fraction: string; cost: string }[] }[] }>(
      `/projects/${projectId}/salary-costs-by-month/`
    ).then(wrap),
};

// ── Payroll Actuals (Urealnienie wypłat) ──────────────────────────────────

export const payrollActualsApi = {
  list: (year: number, month: number) =>
    apiClient
      .get<{ data: MonthlyPayrollActual[] }>("/finances/payroll-actuals/", { params: { year, month } })
      .then(wrap),

  upsert: (data: { company: string; year: number; month: number; net_uop: string; net_uz: string; net_b2b: string; zus: string; pit: string; notes?: string }) =>
    apiClient
      .post<{ data: MonthlyPayrollActual; meta: { created: boolean } }>("/finances/payroll-actuals/upsert/", data)
      .then((r) => r.data),
};

// ── Payroll Theoretical Reference (CEO + Accountant only) ─────────────────

export const payrollTheoreticalApi = {
  list: (year: number, month: number, company?: string) =>
    apiClient
      .get<{ data: TheoreticalCompany[] }>("/finances/payroll-actuals/theoretical/", {
        params: { year, month, ...(company ? { company } : {}) },
      })
      .then(wrap),
};

// ── Order Cost Matrix ─────────────────────────────────────────────────────

export const costMatrixApi = {
  get: (year?: number, month?: number) =>
    apiClient
      .get<{ data: CostMatrixData }>("/finances/order-cost-matrix/", {
        params: {
          ...(year ? { year } : {}),
          ...(month ? { month } : {}),
        },
      })
      .then(wrap),

  // Widok zwinięty do grup zleceń — drobnica łączy się w jeden wiersz na grupę.
  getGrouped: (year?: number, month?: number) =>
    apiClient
      .get<{ data: GroupedCostMatrixData }>("/finances/order-cost-matrix/", {
        params: {
          ...(year ? { year } : {}),
          ...(month ? { month } : {}),
          group_by: "group",
        },
      })
      .then(wrap),
};

// ── Employee Expenses (Faktury kosztowe pracowników) ───────────────────────

export interface ExpenseProxyTarget {
  id: string;
  full_name: string;
  role: string;
  contract_type: string | null;
  /** False for recently-deactivated employees still within the proxy grace period. */
  is_active: boolean;
}

export const employeeExpensesApi = {
  list: () =>
    apiClient.get<{ data: EmployeeExpense[] }>("/finances/employee-expenses/").then(wrap),

  create: (data: {
    expense_type?: string;
    description?: string;
    amount: string;
    /** Netto — wymagane dla faktury comiesięcznej (średnia kosztu liczy się z netto). */
    amount_net?: string;
    currency?: string;
    is_reimbursement?: boolean;
    is_proforma?: boolean;
    invoice_file: File;
    project?: string | null;
    employee?: string | null;
    // Monthly-invoice file for a recurring cost: description/project are inherited
    // from the cost server-side, so they are omitted here.
    recurring_cost?: string | null;
    period_year?: number;
    period_month?: number;
  }) => {
    const form = new FormData();
    form.append("amount", data.amount);
    if (data.amount_net) form.append("amount_net", data.amount_net);
    form.append("currency", data.currency || "PLN");
    form.append("invoice_file", data.invoice_file);
    // A monthly-invoice file can ALSO be a reimbursement — a fixed cost paid from a
    // private account is still owed back to the payer, so the flag rides both paths.
    form.append("is_reimbursement", String(!!data.is_reimbursement));
    if (data.recurring_cost) {
      form.append("recurring_cost", data.recurring_cost);
      form.append("period_year", String(data.period_year));
      form.append("period_month", String(data.period_month));
    } else {
      form.append("expense_type", data.expense_type || "invoice");
      form.append("description", data.description || "");
      form.append("is_proforma", String(!!data.is_proforma));
      if (data.project) form.append("project", data.project);
    }
    // On-behalf submission: the target employee. Omitted → submit for self.
    if (data.employee) form.append("employee", data.employee);
    return apiClient.post<{ data: EmployeeExpense }>("/finances/employee-expenses/", form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(wrap);
  },

  /** Edit a not-yet-accepted (pending) expense. Monthly-invoice files accept
   *  amount + file only (description/project inherited from the recurring cost). */
  update: (id: string, data: {
    expense_type?: string;
    description?: string;
    amount?: string;
    currency?: string;
    is_reimbursement?: boolean;
    is_proforma?: boolean;
    invoice_file?: File | null;
    project?: string | null;
  }) => {
    const form = new FormData();
    if (data.expense_type !== undefined) form.append("expense_type", data.expense_type);
    if (data.description !== undefined) form.append("description", data.description);
    if (data.amount !== undefined) form.append("amount", data.amount);
    if (data.currency !== undefined) form.append("currency", data.currency);
    if (data.is_reimbursement !== undefined) form.append("is_reimbursement", String(data.is_reimbursement));
    if (data.is_proforma !== undefined) form.append("is_proforma", String(data.is_proforma));
    // Empty string clears the project assignment; undefined leaves it untouched.
    if (data.project !== undefined) form.append("project", data.project ?? "");
    if (data.invoice_file) form.append("invoice_file", data.invoice_file);
    return apiClient.patch<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(wrap);
  },

  proxyTargets: () =>
    apiClient
      .get<{ data: ExpenseProxyTarget[]; meta: { count: number } }>("/finances/employee-expenses/proxy_targets/")
      .then((r) => r.data.data),

  delete: (id: string) =>
    apiClient.delete(`/finances/employee-expenses/${id}/`),

  approve: (id: string, director_comment?: string) =>
    apiClient.post<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/approve/`, { director_comment }).then(wrap),

  reject: (id: string, director_comment?: string) =>
    apiClient.post<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/reject/`, { director_comment }).then(wrap),

  prepareTransfer: (id: string) =>
    apiClient.post<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/prepare_transfer/`, {}).then(wrap),

  markPaid: (id: string, paid_at: string) =>
    apiClient.post<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/mark_paid/`, { paid_at }).then(wrap),

  markBooked: (id: string) =>
    apiClient.post<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/mark_booked/`, {}).then(wrap),

  history: (id: string) =>
    apiClient.get<{ data: EmployeeExpenseEvent[] }>(`/finances/employee-expenses/${id}/history/`).then(wrap),

  undoLast: (id: string) =>
    apiClient.post<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/undo-last/`, {}).then(wrap),

  /** Attach an ALREADY FILED expense to a recurring cost as its monthly invoice.
   *  Creating a new file goes through `create` — this is the retro-fit path for an
   *  invoice submitted standalone (recurring_cost/period are read-only on PATCH). */
  linkRecurringCost: (id: string, data: {
    recurring_cost: string;
    period_year: number;
    period_month: number;
  }) =>
    apiClient
      .post<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/link-recurring-cost/`, data)
      .then(wrap),

  unlinkRecurringCost: (id: string) =>
    apiClient
      .post<{ data: EmployeeExpense }>(`/finances/employee-expenses/${id}/unlink-recurring-cost/`, {})
      .then(wrap),
};

// ── Mileage sheet (Kilometrówka) ───────────────────────────────────────────

export interface MileageTrip {
  purpose: string;
  from_location: string;
  to_location: string;
  kilometers: string;
  odometer: string;
  notes: string;
}

export interface MileageDay {
  day: number;
  date_str: string;
  weekday_short: string;
  is_weekend: boolean;
  is_holiday: boolean;
  holiday_name: string;
  trips: MileageTrip[];
  updated_at: string | null;
}

export const mileageSheetApi = {
  get: (year: number, month: number, employee?: string) =>
    apiClient
      .get<{ data: { days: MileageDay[]; year: number; month: number } }>(
        "/finances/employee-expenses/mileage_sheet/",
        { params: employee ? { year, month, employee } : { year, month } },
      )
      .then((r) => r.data.data),

  save: (payload: { year: number; month: number; days: { day: number; trips: MileageTrip[] }[] }) =>
    apiClient
      .post("/finances/employee-expenses/save_mileage_sheet/", payload)
      .then(() => undefined),

  exportPdf: (payload: { year: number; month: number; days: MileageDay[]; employee?: string }) =>
    apiClient
      .post("/finances/employee-expenses/export_mileage_pdf/", payload, { responseType: "blob" })
      .then((r) => r.data as Blob),
};
